export type EnumOption<TValue extends string> = {
  value: TValue;
  label: string;
};

const normalizeEnumToken = (value: string): string =>
  value
    .trim()
    .replace(/[\s-]+/g, '_')
    .toUpperCase();

const enumSignature = (value: string): string =>
  normalizeEnumToken(value).replace(/[^A-Z0-9_]/g, '');

export const isEnumValue = <TValue extends string>(
  value: unknown,
  allowedValues: readonly TValue[]
): value is TValue =>
  typeof value === 'string' && allowedValues.includes(value as TValue);

export const coerceEnumValue = <TValue extends string>(
  value: unknown,
  allowedValues: readonly TValue[]
): TValue | '' => {
  if (typeof value !== 'string') {
    return '';
  }

  const directMatch = allowedValues.find(option => option === value);
  if (directMatch) {
    return directMatch;
  }

  const normalized = normalizeEnumToken(value);
  const normalizedMatch = allowedValues.find(
    option => normalizeEnumToken(option) === normalized
  );
  if (normalizedMatch) {
    return normalizedMatch;
  }

  const signature = enumSignature(value);
  const signatureMatch = allowedValues.find(
    option => enumSignature(option) === signature
  );
  return signatureMatch ?? '';
};

export const coerceEnumArray = <TValue extends string>(
  value: unknown,
  allowedValues: readonly TValue[],
  maxItems?: number
): TValue[] => {
  const rawItems = Array.isArray(value)
    ? value
    : typeof value === 'string'
    ? value.split(',')
    : [];

  const coerced = rawItems
    .map(item => coerceEnumValue(item, allowedValues))
    .filter((item): item is TValue => item !== '');

  const deduped = Array.from(new Set(coerced));
  return typeof maxItems === 'number' ? deduped.slice(0, maxItems) : deduped;
};

export const buildEnumOptions = <TValue extends string>(
  values: readonly TValue[],
  labels: Record<TValue, string>
): EnumOption<TValue>[] =>
  values.map(value => ({
    value,
    label: labels[value],
  }));
